import { useEffect, useState } from "react";
import axios from "axios";
import { DollarSign, ShoppingBag, Users, Globe, MapPin, MessageSquare, TrendingUp } from "lucide-react";
import { useLang } from "@/lib/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const T = lang === "fr" ? FR : EN;

  useEffect(() => {
    axios.get(`${API}/admin/dashboard`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.detail || "Erreur"));
  }, []);

  if (error) return null;
  if (!data) return <div className="text-center py-6 text-zinc-500">{T.loading}</div>;

  const { sales, stats } = data;
  const maxChart = Math.max(...sales.chart_30d.map(c => c.amount), 1);

  return (
    <section className="border border-[#8b6f3d] rounded-2xl p-6 md:p-8 bg-gradient-to-br from-amber-50/70 to-white mt-8" data-testid="admin-dashboard">
      <div className="mb-4 flex items-center gap-3 text-[#8b6f3d]">
        <div className="h-px w-12 bg-[#8b6f3d]/40" />
        <span className="font-mono-data text-[11px] uppercase tracking-[0.3em]">{T.tag}</span>
      </div>
      <h2 className="font-serif-mystery text-3xl mb-6">💰 {T.title}</h2>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Kpi icon={<ShoppingBag size={18} />} value={sales.total_orders} label={T.totalOrders} testId="admin-kpi-orders" />
        <Kpi icon={<DollarSign size={18} />} value={`${sales.total_revenue.toFixed(2)} ${sales.currency}`} label={T.revenue} testId="admin-kpi-revenue" />
        <Kpi icon={<Users size={18} />} value={stats.total_users} label={T.users} testId="admin-kpi-users" />
        <Kpi icon={<MapPin size={18} />} value={stats.total_visits} label={T.visits} testId="admin-kpi-visits" />
      </div>

      {/* Chart 30d */}
      <div className="mb-8 border border-zinc-200 rounded-xl p-4 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} className="text-[#8b6f3d]" />
          <h3 className="font-serif-mystery text-lg">{T.chart30d}</h3>
        </div>
        {sales.chart_30d.length === 0 ? (
          <p className="text-sm text-zinc-500 italic">{T.noChart}</p>
        ) : (
          <div className="flex items-end gap-1 h-32" data-testid="admin-chart">
            {sales.chart_30d.map(c => (
              <div key={c.date} className="flex-1 min-w-[6px] flex flex-col items-center justify-end group relative">
                <div
                  className="w-full bg-[#8b6f3d] rounded-t hover:bg-[#a1854b] transition-colors"
                  style={{ height: `${(c.amount / maxChart) * 100}%` }}
                />
                <div className="absolute bottom-full mb-1 hidden group-hover:block bg-zinc-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                  {c.date} · {c.amount.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* By country */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-zinc-200 rounded-xl p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <Globe size={16} className="text-[#8b6f3d]" />
            <h3 className="font-serif-mystery text-lg">{T.byCountry}</h3>
          </div>
          {sales.by_country.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">{T.noSales}</p>
          ) : sales.by_country.map(c => (
            <div key={c.country} className="flex justify-between py-1 border-b border-zinc-100 text-sm last:border-0">
              <span>{c.country}</span>
              <span className="font-mono-data text-[#8b6f3d]">{c.count}</span>
            </div>
          ))}
        </div>

        <div className="border border-zinc-200 rounded-xl p-4 bg-white">
          <div className="flex items-center gap-2 mb-3">
            <ShoppingBag size={16} className="text-[#8b6f3d]" />
            <h3 className="font-serif-mystery text-lg">{T.recent}</h3>
          </div>
          {sales.recent.length === 0 ? (
            <p className="text-sm text-zinc-500 italic">{T.noSales}</p>
          ) : sales.recent.slice(0, 5).map(o => (
            <div key={o.order_number} className="py-2 border-b border-zinc-100 last:border-0" data-testid={`admin-order-${o.order_number}`}>
              <div className="flex justify-between items-start">
                <div className="min-w-0 flex-1">
                  <div className="font-mono-data text-xs text-[#8b6f3d]">{o.order_number}</div>
                  <div className="text-sm truncate">{o.customer} · {o.country}</div>
                  <div className="text-xs text-zinc-500 truncate">{o.email}</div>
                </div>
                <div className="text-sm font-medium ml-2">{o.total.toFixed(2)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500">
        <MessageSquare size={12} /> {stats.total_comments} {T.commentsPosted} · {stats.total_locations} {T.locationsMapped}
      </div>
    </section>
  );
}

function Kpi({ icon, value, label, testId }) {
  return (
    <div className="border border-zinc-200 rounded-xl p-4 bg-white" data-testid={testId}>
      <div className="w-9 h-9 rounded-full bg-[#8b6f3d]/10 text-[#8b6f3d] flex items-center justify-center mb-2">{icon}</div>
      <div className="font-serif-mystery text-2xl text-zinc-900">{value}</div>
      <div className="font-mono-data text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}

const FR = {
  tag: "Espace Administrateur",
  title: "Ventes du site",
  loading: "Chargement…",
  totalOrders: "Ventes totales",
  revenue: "Chiffre d'affaires",
  users: "Utilisateurs",
  visits: "Visites GPS",
  chart30d: "Ventes des 30 derniers jours",
  byCountry: "Ventes par pays",
  recent: "Commandes récentes",
  noChart: "Aucune vente pour le moment. Le graphique se remplira avec les premières commandes.",
  noSales: "Aucune vente pour le moment.",
  commentsPosted: "commentaires publiés",
  locationsMapped: "lieux cartographiés",
};
const EN = {
  tag: "Administrator area",
  title: "Site sales",
  loading: "Loading…",
  totalOrders: "Total orders",
  revenue: "Revenue",
  users: "Users",
  visits: "GPS visits",
  chart30d: "Sales — last 30 days",
  byCountry: "Sales by country",
  recent: "Recent orders",
  noChart: "No sales yet. The chart will fill with your first orders.",
  noSales: "No sales yet.",
  commentsPosted: "comments posted",
  locationsMapped: "locations mapped",
};
